function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _objectWithoutProperties(obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
}

import objectReduce from 'fast-loops/lib/objectReduce';
import cssifyObject from 'css-in-js-utils/lib/cssifyObject';
import isPlainObject from 'isobject';
import { isSupport, isMediaQuery, isNestedSelector, isUndefinedValue, normalizeNestedProperty, processStyleWithPlugins, generateCombinedMediaQuery, generateCSSSelector, RULE_TYPE } from 'fela-utils';
import generateMonolithicClassName from './generateMonolithicClassName';

function useMonolithicRenderer(renderer) {
  var prettySelectors = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  renderer.prettySelectors = prettySelectors;

  renderer._renderStyleToCache = function (className, style) {
    var pseudo = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '';
    var media = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : '';
    var support = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : '';
    var ruleSet = objectReduce(style, function (ruleset, value, property) {
      if (isPlainObject(value)) {
        if (isNestedSelector(property)) {
          renderer._renderStyleToCache(className, value, pseudo + normalizeNestedProperty(property), media, support);
        } else if (isMediaQuery(property)) {
          var combinedMediaQuery = generateCombinedMediaQuery(media, property.slice(6).trim());

          renderer._renderStyleToCache(className, value, pseudo, combinedMediaQuery, support);
        } else if (isSupport(property)) {
          var combinedSupport = generateCombinedMediaQuery(support, property.slice(9).trim());

          renderer._renderStyleToCache(className, value, pseudo, media, combinedSupport);
        } else {
          console.warn('The object key "' + property + '" is not a valid nested key in Fela. \nMaybe you forgot to add a plugin to resolve it? \nCheck http://fela.js.org/docs/basics/Rules.html#styleobject for more information.');
        }
      } else if (!isUndefinedValue(value)) {
        ruleset[property] = value;
      }

      return ruleset;
    }, {});

    if (Object.keys(ruleSet).length > 0) {
      var css = cssifyObject(ruleSet);
      var selector = generateCSSSelector(className, pseudo, renderer.specificityPrefix);
      var change = {
        type: RULE_TYPE,
        className: className,
        selector: selector,
        declaration: css,
        media: media,
        pseudo: pseudo,
        support: support
      };
      var declarationReference = selector + media + support;
      renderer.cache[declarationReference] = change;

      renderer._emitChange(change);
    }
  };

  renderer._renderStyleToClassNames = function (_ref, rule) {
    var _className = _ref._className,
        style = _objectWithoutProperties(_ref, ['_className']);

    if (Object.keys(style).length < 1) {
      return '';
    }

    var localRulePrefix = renderer.prettySelectors && (rule.ruleName || rule.name) ? (rule.ruleName || rule.name) + '_' : '';
    var className = generateMonolithicClassName(style, (renderer.selectorPrefix || '') + (rule.selectorPrefix || localRulePrefix));

    if (!renderer.cache.hasOwnProperty(className)) {
      renderer._renderStyleToCache(className, style);

      renderer.cache[className] = {};
    }

    return (_className ? _className + ' ' : '') + className;
  };

  renderer.renderRule = function (rule) {
    var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    return renderer._renderStyle(rule(props, renderer), props, rule);
  };

  renderer._renderStyle = function () {
    var style = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var rule = arguments[2];
    var processedStyle = processStyleWithPlugins(renderer, style, RULE_TYPE, props);
    return renderer._renderStyleToClassNames(processedStyle, rule || {});
  };

  renderer.subscribe(function (event) {
    if (event.type === 'REHYDRATATION_FINISHED') {
      // Repair cache for monolithic usage
      renderer.cache = Object.keys(renderer.cache).reduce(function (acc, key) {
        var item = renderer.cache[key];

        if (item.type === 'RULE') {
          return Object.assign(acc, _defineProperty({}, item.className + item.media + item.support, item));
        }

        return Object.assign(acc, _defineProperty({}, key, item));
      }, {});
    }
  });
  return renderer;
}

export default function monolithic() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return function (renderer) {
    return useMonolithicRenderer(renderer, process.env.NODE_ENV !== 'production' && options.prettySelectors);
  };
}